import { Pipe, PipeTransform } from '@angular/core';
import {
  isValidTime,
  formatTimeRange,
  getTimeDifference
} from '../../helpers/time.utils';

export interface TimeRangePipeOptions {
  /**
   * @deprecated
   */
  addHourCounter?: boolean;
  /**
   * Adds the highest unit
   */
  addUnitCounter?: boolean;
  withColons?: boolean;
  /**
   * IANA timezone used to render date-time values. Wall-clock-only values (HH:mm) are
   * rendered verbatim — they are already expressed in the target zone.
   */
  timeZone?: string;
}

/**
 * Transform and start date and a end date into a range of hours.
 *
 * @example
 * ```typescript
 *  ['2025-01-13T08:00:45','2025-01-13T16:03:45'] | timeRange => 0800 - 1603
 *
 * ```
 */
@Pipe({ name: 'timeRange', standalone: true })
export class TimeRangePipe implements PipeTransform {
  transform(
    value: [string | null | undefined, string | null | undefined],
    options?: TimeRangePipeOptions
  ): string {
    if (value.some((v) => v == null || !isValidTime(v))) return '';
    const start = value[0] as string;
    const end = value[1] as string;

    const result = formatTimeRange(
      start,
      end,
      options?.withColons ?? true,
      options?.timeZone
    );

    if (!result) return '';
    if (options?.addHourCounter) {
      const diff = getTimeDifference(start, end);
      if (diff === null) return `${result}`;
      const extraHours = diff.minutes > 30 ? 1 : 0;
      const totalHours = diff.hours + extraHours;
      return `${result} (${totalHours}h)`;
    }

    if (options?.addUnitCounter) {
      const diff = getTimeDifference(start, end);
      if (!diff) return `${result}`;
      if (diff.hours > 0) {
        const extra = diff.minutes > 30 ? 1 : 0;
        const total = diff.hours + extra;
        return `${result} (${total}h)`;
      }
      if (diff.minutes > 0) {
        const extra = diff.seconds > 30 ? 1 : 0;
        const total = diff.minutes + extra;
        return `${result} (${total}m)`;
      }
      if (diff.seconds > 0) {
        const total = diff.seconds;
        return `${result} (${total}s)`;
      }
    }

    return result;
  }
}
